import type { PatchMe } from '@/types/patching'

/**
 * Who may change what on a saved Azure connection, in one place. The server enforces the same rules (it answers 403), so
 * this only keeps the page honest: it does not hide anything the server would have allowed.
 *
 * Patch administrators (`is_patch_admin`: a Django superuser or a user whose role is admin) may edit and delete a connection that
 * already exists. Everyone else who may use the page can add a connection, validate it and discover its machines, but an
 * existing connection is read-only for them. Anyone who can add a connection is reported by `can_request`, not here.
 */
export function canEditConnection(me: PatchMe | null | undefined): boolean {
  return me?.is_patch_admin === true
}

/** Deleting is the same right as editing today. It has its own function so the two can be split without touching the page. */
export function canDeleteConnection(me: PatchMe | null | undefined): boolean {
  return canEditConnection(me)
}

/** Shown beside a connection the user may not change. */
export const CONNECTION_READ_ONLY_NOTE = 'Only admins can edit this connection. You can validate it and discover resources.'

/** The tooltip on Save configuration while it is disabled for that reason. */
export const CONNECTION_SAVE_BLOCKED_REASON = 'Only admins can save changes to an existing connection.'

/** The tooltip on Delete connection while it is disabled for that reason. */
export const CONNECTION_DELETE_BLOCKED_REASON = 'Only admins can delete a connection.'

/** What the page says when the server answers 403 to a delete. */
export const CONNECTION_DELETE_DENIED = 'You do not have permission to delete this connection. Only admins can delete a connection.'

/** What the page says when the server answers 403 to a save and gives no reason of its own. */
export const CONNECTION_SAVE_DENIED = 'You do not have permission to change this connection. Only admins can edit an existing connection.'
